import React from 'react'

import { ShoppingBag } from '@mui/icons-material'
import { Box, Divider, Typography } from '@mui/material'

import ResponsiveDisplayWrapper from '../ResponsiveDisplayWrapper/ResponsiveDisplayWrapper.component'
import OverviewBreakdownSection from './components/OverviewBreakdownSection/OverviewBreakdownSection.component'
import RecipientWidget from './components/RecipientWidget/RecipientWidget.component'
import SummaryRows from './components/SummaryRows/SummaryRows.component'
import TotalSection from './components/TotalSection/TotalSection.component'

import { CartItem } from '../../../pages/Cart/Cart.types'
import {
  FulfillmentOrderItem,
  RecipientDetailsValues
} from '../../../pages/CreateFulfillmentOrder/CreateFulfillmentOrder.types'
import styles from './Overview.styles'
import { CartOverviewProps, FulfillmentOrderOverviewProps, OverviewProps } from './Overview.types'

const hasRecipientDisplayData = (recipient: RecipientDetailsValues): boolean =>
  !!(
    recipient.firstName?.trim() ||
    recipient.lastName?.trim() ||
    recipient.phone?.trim() ||
    recipient.city?.trim() ||
    recipient.country?.trim()
  )

const Overview: React.FC<OverviewProps> = (props) => {
  const { currency, tax, fees, discount, subtotal, total, itemType, showSubtotal = true } = props

  const isCartProps = (overviewProps: OverviewProps): overviewProps is CartOverviewProps =>
    overviewProps.itemType === 'cart'
  const isFulfillmentProps = (overviewProps: OverviewProps): overviewProps is FulfillmentOrderOverviewProps =>
    overviewProps.itemType === 'fulfillment'

  const overviewCurrency = currency ?? (isCartProps(props) && props.items.length > 0 ? props.items[0].currency : 'USD')

  const calculateCartTotals = () => {
    if (itemType !== 'cart') return { totalItems: 0, calculatedSubtotal: 0 }

    const cartItems = props.items as CartItem[]
    const totalItems = cartItems
      .filter((item) => item.stockStatus !== 'Out of Stock')
      .reduce((sum, item) => sum + item.quantity, 0)

    const calculatedSubtotal =
      subtotal ??
      cartItems
        .filter((item) => item.stockStatus !== 'Out of Stock')
        .reduce((sum, item) => sum + item.price * item.quantity, 0)

    return { totalItems, calculatedSubtotal }
  }

  const calculateFulfillmentTotals = () => {
    if (itemType !== 'fulfillment') return { totalItems: 0, calculatedSubtotal: 0 }

    const fulfillmentItems = props.items as FulfillmentOrderItem[]
    const totalItems = fulfillmentItems.reduce((sum, item) => sum + item.quantity, 0)
    const calculatedSubtotal = subtotal ?? 0

    return { totalItems, calculatedSubtotal }
  }

  const { totalItems, calculatedSubtotal } = itemType === 'cart' ? calculateCartTotals() : calculateFulfillmentTotals()

  const overviewTotal = total ?? calculatedSubtotal + (fees ?? 0) + (tax ?? 0)

  const fulfillmentItems = isFulfillmentProps(props) ? (props.items as FulfillmentOrderItem[]) : []
  const fulfillmentPackageDetails = isFulfillmentProps(props) ? props.packageDetails : null
  const hasFulfillmentItems = isFulfillmentProps(props) && fulfillmentItems.length > 0

  const fulfillmentBreakdownDetails =
    isFulfillmentProps(props) && hasFulfillmentItems
      ? {
          totalItems,
          totalWeightGrams: fulfillmentPackageDetails?.totalWeightGrams,
          lengthCm: fulfillmentPackageDetails?.lengthCm,
          widthCm: fulfillmentPackageDetails?.widthCm,
          heightCm: fulfillmentPackageDetails?.heightCm
        }
      : undefined

  return (
    <Box sx={styles.container}>
      <ResponsiveDisplayWrapper isDesktop={true}>
        <Typography variant="h6" sx={styles.title}>
          {isFulfillmentProps(props) && props.title ? props.title : 'Overview'}
        </Typography>
      </ResponsiveDisplayWrapper>

      {/* Empty state – fulfillment only, when no items added yet */}
      {isFulfillmentProps(props) && !hasFulfillmentItems && (
        <Box sx={styles.emptyState}>
          <ShoppingBag sx={styles.emptyStateIcon} />
          <Typography sx={styles.emptyStateText}>No items added yet</Typography>
          <Typography sx={styles.emptyStateHint}>Add items on the left to see your order summary</Typography>
        </Box>
      )}

      {/* Recipient – fulfillment only */}
      {isFulfillmentProps(props) && props.recipient && hasRecipientDisplayData(props.recipient) && (
        <>
          <Divider sx={styles.divider} />
          <RecipientWidget recipient={props.recipient} onEditRecipient={props.onEditRecipient} />
        </>
      )}

      <Box>
        {/* Subtotal section */}
        {showSubtotal && (
          <OverviewBreakdownSection
            subtotal={calculatedSubtotal}
            currency={overviewCurrency}
            totalItems={totalItems}
            total={total}
            mobileButton={props.getActiveButton?.()}
            fulfillmentOrderPackageDetails={fulfillmentBreakdownDetails}
            mobileAccordionBody={
              props.mobileAccordionBodyPrefix || !isFulfillmentProps(props) || tax || fees || discount ? (
                <>
                  {props.mobileAccordionBodyPrefix}

                  {(!isFulfillmentProps(props) || tax || fees || discount) && (
                    <SummaryRows
                      tax={tax}
                      fees={fees}
                      discount={discount}
                      currency={overviewCurrency}
                      desktopOnly={false}
                    />
                  )}
                </>
              ) : undefined
            }
          />
        )}

        {/* Summary rows (tax, fees, discount) – desktop only; mobile includes via OverviewBreakdownSection Accordion */}
        {(tax || fees || discount) && (
          <SummaryRows tax={tax} fees={fees} discount={discount} currency={overviewCurrency} desktopOnly={true} />
        )}

        {/* Total section */}
        {total !== undefined && total !== null && (
          <TotalSection total={overviewTotal} currency={overviewCurrency} showAmountToPay={!!total} />
        )}

        {props.children}
      </Box>
    </Box>
  )
}

export default Overview
